/**
 * PhotoSubmitModal — universal "📷 Share Your Photos" contribution
 * modal used on articles, travel operator pages, and anywhere users
 * can enrich a place with images.
 *
 * Props:
 *   targetType: 'article' | 'travel_operator' | 'business' | 'location' | 'general'
 *   targetSlug: optional slug tied to the target
 *   targetTitle: optional string shown as context ("for Alaska Airlines")
 *   trigger: optional replacement trigger button
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, Loader2, X, MapPin, Tag as TagIcon } from "lucide-react";
import VideoLinkManager from "./VideoLinkManager";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 6 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = reader.result;
      // strip "data:image/xxx;base64," prefix
      const idx = raw.indexOf(",");
      resolve(raw.slice(idx + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function tryGeo() {
  return new Promise(resolve => {
    if (!("geolocation" in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, maximumAge: 60000 }
    );
  });
}

export default function PhotoSubmitModal({ targetType = "general", targetSlug, targetId, targetTitle, trigger }) {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", country: "", state: "", city: "",
    tags: "", notes: "",
  });
  const [videoUrls, setVideoUrls] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP, or GIF."); return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Image too large (max ${MAX_BYTES / 1024 / 1024} MB).`); return;
    }
    setFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    // Auto-fill title if empty
    if (!f.title) setF(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, "") }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a photo."); return; }
    if (!f.title.trim()) { toast.error("Please add a short title."); return; }
    setSaving(true);
    try {
      const data_base64 = await fileToBase64(file);
      const geo = await tryGeo();
      const captured_at = file.lastModifiedDate ? new Date(file.lastModifiedDate).toISOString() : new Date().toISOString();
      // Append any linked video URLs to editor notes so moderators see them
      // alongside the photo submission. (Native video-submission storage
      // will come in a follow-up.)
      const notesWithVideos = [
        f.notes.trim() || null,
        videoUrls.length ? `Linked videos:\n${videoUrls.join("\n")}` : null,
      ].filter(Boolean).join("\n\n") || null;
      await axios.post(`${API}/api/photos/submit`, {
        target_type: targetType,
        target_slug: targetSlug || null,
        target_id: targetId || null,
        title: f.title.trim(),
        description: f.description.trim() || null,
        country: f.country.trim() || null,
        state: f.state.trim() || null,
        city: f.city.trim() || null,
        tags: f.tags.split(",").map(t => t.trim()).filter(Boolean),
        notes: notesWithVideos,
        gps_lat: geo?.lat ?? null,
        gps_lon: geo?.lon ?? null,
        captured_at,
        mime_type: file.type,
        data_base64,
      }, {
        headers: { Authorization: `Bearer ${token || ""}` },
      });
      toast.success("Thanks! We received your photo — you'll hear from us soon.");
      setOpen(false); setFile(null); setPreview(null);
      setF({ title: "", description: "", country: "", state: "", city: "", tags: "", notes: "" });
      setVideoUrls([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  const openModal = () => {
    if (!user) { toast.info("Sign in to share photos."); return; }
    setOpen(true);
  };

  return (
    <>
      {trigger ? (
        <span onClick={openModal} data-testid="photo-submit-trigger">{trigger}</span>
      ) : (
        <button
          onClick={openModal}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs md:text-sm border hover:shadow-md transition"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          data-testid="photo-submit-button"
        >
          <Camera className="w-3.5 h-3.5" />
          <span>📷 Share Your Photos</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setOpen(false)}>
          <div
            onClick={e => e.stopPropagation()}
            className="wofy-card !p-0 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="photo-submit-modal"
          >
            <div className="px-6 pt-6 pb-2 flex items-start justify-between gap-4 sticky top-0" style={{ background: "var(--wofy-bg)" }}>
              <div>
                <h2 className="font-display text-2xl">📷 Share Your Photos</h2>
                <p className="text-sm opacity-80 mt-1">
                  Help make {targetTitle ? <b>{targetTitle}</b> : "this place"} richer by sharing your photos. Whether it's a landmark, scenery, sunset, plant, flower, event, local business, wildlife, food, or a special moment — your images help others discover and appreciate it.
                </p>
                <p className="text-xs opacity-70 mt-1">Every approved photo is credited to its contributor.</p>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/60" data-testid="photo-modal-close"><X className="w-4 h-4" /></button>
            </div>

            {!user && (
              <div className="mx-6 mb-4 p-3 rounded-lg text-sm" style={{ background: "#FEF3C7", color: "#78350F" }}>
                Please <Link to="/auth" className="wofy-link">sign in</Link> so we can credit and email you the review outcome.
              </div>
            )}

            <form onSubmit={submit} className="px-6 pb-6 space-y-3">
              {/* File picker */}
              <div>
                <label className="block text-sm font-medium mb-1">📤 Upload photo *</label>
                {preview ? (
                  <div className="relative rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                    <img src={preview} alt="preview" className="w-full max-h-64 object-cover" />
                    <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center" data-testid="photo-remove">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center gap-2 p-6 rounded-lg border-2 border-dashed cursor-pointer hover:bg-white/40" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-file-picker">
                    <Upload className="w-8 h-8 opacity-40" />
                    <span className="text-sm opacity-70">JPG · PNG · WEBP · GIF (max 6 MB)</span>
                    <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="photo-file-input" />
                    <span className="text-xs px-3 py-1 rounded-full text-white" style={{ background: "var(--wofy-primary)" }}>Choose file</span>
                  </label>
                )}
              </div>

              {/* Video links — next to the Upload Photo caption per product spec */}
              <div className="pt-1">
                <VideoLinkManager
                  value={videoUrls}
                  onChange={setVideoUrls}
                  label="Link a video (optional)"
                  helper="Paste a YouTube or Vimeo URL. We'll embed it alongside the photo once your submission is approved."
                  max={3}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input required value={f.title} onChange={set("title")} maxLength={200}
                    className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-title" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1"><TagIcon className="w-3 h-3 inline" /> Tags (comma-separated)</label>
                  <input value={f.tags} onChange={set("tags")}
                    className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }}
                    placeholder="sunset, coastline, kerala" data-testid="photo-tags" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Short description</label>
                <textarea rows={2} value={f.description} onChange={set("description")} maxLength={1500}
                  className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-description" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1"><MapPin className="w-3 h-3 inline" /> Location</label>
                <div className="grid gap-2 sm:grid-cols-3">
                  <input value={f.country} onChange={set("country")} placeholder="Country" className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-country" />
                  <input value={f.state} onChange={set("state")} placeholder="State/Region" className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-state" />
                  <input value={f.city} onChange={set("city")} placeholder="City / Village" className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-city" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes for our editors</label>
                <textarea rows={2} value={f.notes} onChange={set("notes")} maxLength={1500}
                  className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="photo-notes" />
              </div>

              <div className="text-xs opacity-70">
                We'll also automatically capture: your name (for credit), timestamp, and (with permission) GPS location.
                Every submission is reviewed before publishing.
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full border text-sm" style={{ borderColor: "var(--wofy-border)" }}>Cancel</button>
                <button type="submit" disabled={saving || !user || !file || !f.title.trim()} className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="photo-submit-btn">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading…</> : <><Upload className="w-4 h-4" />Submit for review</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
